import * as React from 'react';
import type LocationCommonWellItemViewState from 'owa-location-common-well/lib/store/schema/LocationCommonWellItemViewState';
import {
    usePatchPartialPayload,
    useAssetCommands,
    AssetCommandDescriptors,
    AssetErrorType,
    useProjectId,
    useAssetId,
    showNotification,
} from 'owa-timestream-asset-sdk';
import type { LocationPayload } from '../schema';
import getPlace from '../utils/getPlace';
import getDirections from '../utils/getDirections';
import getImageUrl from '../utils/getImageUrl';
import loc from 'owa-localize';
import {
    noPhoneNumbers,
    noBusinessUrls,
    noAddresses,
    addressCopied,
} from '../location.locstring.json';
import { focusAsset } from 'owa-timestream-asset-sdk-actions';

export default function useLocationViewState({
    selectedLocation,
    locationDetails,
}: LocationPayload) {
    const [imageUrl, setImageUrl] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const pickerRef = React.useRef<HTMLDivElement | null>(null);
    const [isPickerVisible, setIsPickerVisible] = React.useState(
        !selectedLocation?.persona.personaControlId
    );
    const [errorType, setErrorType] = React.useState<AssetErrorType | undefined>(undefined);
    const patchPartialPayload = usePatchPartialPayload<LocationPayload>();
    const projectId = useProjectId();
    const assetId = useAssetId();

    React.useEffect(() => {
        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [pickerRef.current, selectedLocation?.persona.personaControlId]);

    const handleOutsideClick = React.useCallback(
        (event: MouseEvent) => {
            if (!pickerRef.current || pickerRef?.current?.contains(event.target as Node)) {
                return;
            }
            !!selectedLocation?.persona.personaControlId
                ? setIsPickerVisible(false)
                : setIsPickerVisible(true);
        },
        [pickerRef.current, selectedLocation?.persona.personaControlId]
    );

    const setPickerVisible = React.useCallback(() => {
        setIsPickerVisible(true);
    }, []);

    const onLocationsChangedCallback = React.useCallback(
        (items: LocationCommonWellItemViewState[]) => {
            const item = items?.length > 0 ? items[0] : undefined;
            patchPartialPayload({ selectedLocation: item });
            if (!item) {
                setImageUrl('');
                patchPartialPayload({ locationDetails: undefined });
            } else {
                focusAsset(projectId, assetId);
            }
        },
        [projectId, assetId]
    );

    const onRetryGetPlace = React.useCallback(() => {
        if (!!selectedLocation?.persona.personaControlId) {
            setIsLoading(true);
            getPlace(selectedLocation, setErrorType).then(response => {
                patchPartialPayload({ locationDetails: response });
                getImageUrl(response, setImageUrl);
                setIsLoading(false);
            });
            setIsPickerVisible(false);
        }
    }, [selectedLocation?.persona.personaControlId]);

    React.useEffect(() => {
        if (!!selectedLocation?.persona.personaControlId && !!locationDetails?.Name) {
            getImageUrl(locationDetails, setImageUrl);
        }
        if (!locationDetails?.Name) {
            onRetryGetPlace();
        }
    }, [selectedLocation?.persona.personaControlId]);

    const address = selectedLocation?.persona?.displayAddress;
    useAssetCommands(
        [
            {
                ...AssetCommandDescriptors.copyAddress,
                onInvoke: () => {
                    navigator.clipboard.writeText(address ?? loc(noAddresses));
                    showNotification(
                        'locationAddressCopied',
                        address ? loc(addressCopied) : loc(noAddresses)
                    );
                },
                disabled: !address,
            },
            {
                ...AssetCommandDescriptors.openDirections,
                onInvoke: () => window.open(getDirections(locationDetails)),
                disabled: !locationDetails,
            },
        ],
        [locationDetails?.Name, address]
    );

    return {
        onLocationsChangedCallback,
        imageUrl,
        isLoading,
        phoneNumber: !locationDetails?.PhoneNumber
            ? loc(noPhoneNumbers)
            : locationDetails?.PhoneNumber,
        businessUrl: !locationDetails?.PlaceDetails?.BusinessUrl
            ? loc(noBusinessUrls)
            : locationDetails?.PlaceDetails?.BusinessUrl,
        address: !selectedLocation?.persona?.displayAddress
            ? loc(noAddresses)
            : selectedLocation?.persona?.displayAddress,
        directions: getDirections(locationDetails),
        pickerRef,
        isPickerVisible,
        setPickerVisible,
        errorType,
        onRetryGetPlace,
    };
}
